import { useEffect, useRef, useState } from "react";
import type { AnswerFeedback, PublicQuestion } from "@shared/types";
import { CATEGORY_LABELS, DIFFICULTY_LABELS } from "@shared/config";

interface Props {
  question: PublicQuestion;
  feedback: AnswerFeedback | null;
  selectedAnswer: string | null;
  submitting: boolean;
  onSubmit: (answer: string) => void;
  onNext: () => void;
}

const KEYS = ["1", "2", "3", "4", "5", "6"];

export default function QuestionCard({
  question,
  feedback,
  selectedAnswer,
  submitting,
  onSubmit,
  onNext,
}: Props) {
  const [typed, setTyped] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const revealing = feedback !== null;

  // Reset per-question input and focus.
  useEffect(() => {
    setTyped("");
    if (question.type === "type-in" && !revealing) {
      inputRef.current?.focus();
    }
  }, [question.index, question.type, revealing]);

  // Keyboard: 1-4 to answer multiple-choice; Space/Enter to advance on reveal.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (revealing) {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          onNext();
        }
        return;
      }
      if (
        question.type === "multiple-choice" &&
        !submitting &&
        KEYS.includes(e.key)
      ) {
        const idx = KEYS.indexOf(e.key);
        if (idx < question.options.length) {
          e.preventDefault();
          onSubmit(question.options[idx]);
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [question, revealing, submitting, onSubmit, onNext]);

  const correctNorm = feedback?.correctAnswer.trim().toLowerCase();

  function optionClass(option: string): string {
    const base =
      "flex w-full items-center gap-3 rounded-2xl border-[4px] border-ink px-4 py-3 text-left text-base font-semibold text-ink transition-transform duration-100 sm:text-lg";
    if (!revealing) {
      return `${base} bg-white shadow-[0_5px_0_#1c2a63] hover:-translate-y-0.5 active:translate-y-[3px] active:shadow-[0_2px_0_#1c2a63]`;
    }
    const isCorrect = option.trim().toLowerCase() === correctNorm;
    const isPicked = option === selectedAnswer;
    if (isCorrect) return `${base} bg-grass text-white shadow-[0_5px_0_#14823f]`;
    if (isPicked) return `${base} bg-flame text-white shadow-[0_5px_0_#1c2a63]`;
    return `${base} bg-white opacity-50 shadow-[0_5px_0_#1c2a63]`;
  }

  return (
    <div className="panel animate-pop-in p-4 sm:p-6">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {question.categories.map((c) => (
          <span
            key={c}
            className="rounded-full border-[2px] border-ink bg-sunny px-2.5 py-0.5 text-xs font-bold text-ink"
          >
            {CATEGORY_LABELS[c as keyof typeof CATEGORY_LABELS] ?? c}
          </span>
        ))}
        <span className="rounded-full border-[2px] border-ink bg-grape px-2.5 py-0.5 text-xs font-bold text-white">
          {DIFFICULTY_LABELS[question.difficulty]}
        </span>
        <span className="rounded-full border-[2px] border-ink/30 px-2.5 py-0.5 text-xs font-bold text-ink/45">
          {question.translation}
        </span>
      </div>

      <h2 className="mb-5 text-xl font-bold leading-snug text-ink sm:text-2xl">
        {question.prompt}
      </h2>

      {question.type === "multiple-choice" ? (
        <div className="grid gap-3">
          {question.options.map((option, i) => (
            <button
              key={option}
              type="button"
              disabled={submitting || revealing}
              onClick={() => onSubmit(option)}
              className={optionClass(option)}
            >
              <span
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border-[2px] border-ink bg-white/80 font-display text-sm text-ink"
                aria-hidden
              >
                {i + 1}
              </span>
              <span>{option}</span>
            </button>
          ))}
          {!revealing && (
            <p className="pt-0.5 text-center text-xs font-semibold text-ink/40">
              Tap an answer, or press 1–{question.options.length} on your keyboard.
            </p>
          )}
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!submitting && !revealing && typed.trim()) onSubmit(typed.trim());
          }}
          className="flex flex-col gap-3 sm:flex-row"
        >
          <input
            ref={inputRef}
            type="text"
            value={typed}
            disabled={submitting || revealing}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="Type your answer…"
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            aria-label="Your answer"
            className="flex-1 rounded-2xl border-[4px] border-ink bg-white px-4 py-3 text-lg font-semibold text-ink outline-none placeholder:text-ink/30 focus:border-flame"
          />
          <button
            type="submit"
            disabled={submitting || revealing || !typed.trim()}
            className="btn-chunk bg-grass px-6 text-white"
          >
            Submit
          </button>
        </form>
      )}

      {revealing && feedback && (
        <div
          className={`mt-5 rounded-2xl border-[4px] p-4 ${
            feedback.correct
              ? "border-grass-dark bg-grass/15"
              : "border-flame bg-flame/10"
          }`}
          aria-live="polite"
        >
          <div
            className={`mb-1 font-display text-xl ${
              feedback.correct ? "text-grass-dark" : "text-flame"
            }`}
          >
            {feedback.correct
              ? "Correct! 🎉"
              : feedback.tooFast
                ? "Too fast — read it through!"
                : feedback.timedOut
                  ? "Time's up on that one."
                  : "Not quite!"}
          </div>
          {!feedback.correct && (
            <div className="text-ink/80">
              Answer: <span className="font-bold">{feedback.correctAnswer}</span>
            </div>
          )}
          {feedback.explanation && (
            <p className="mt-1 text-sm text-ink/70">{feedback.explanation}</p>
          )}
          {feedback.references.length > 0 && (
            <p className="mt-1 text-sm font-bold text-grape">
              {feedback.references.join(" · ")}
            </p>
          )}
          <button type="button" onClick={onNext} className="btn-chunk mt-3 bg-flame text-white">
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
